// @givmo/board — board-packet decode + display helpers.
//
// Four model facts drive everything here, and each one is easy to get wrong from UI instinct alone:
//
//   * A PACKET IS A VERSION, NOT A DOCUMENT. One row per version; at most one draft per meeting.
//     Publishing FREEZES that version, and a change opens the NEXT one — which starts EMPTY, because
//     the backend's `create_packet` only takes the next version number. So the UI never offers to
//     "edit a published packet", and it never implies a new version inherits the old one's entries.
//   * ENTRIES ARE FILTERED SERVER-SIDE, AND A GAP IS NOT VISIBLE. `packet_projection` drops an entry
//     naming a record this reader may not read — ABSENT, not a null id and not a refusal. So nothing
//     here counts entries against an expected total, and no surface says "2 items hidden": the reader
//     is not entitled to learn that the withheld thing exists.
//   * `email_sent: false` IS NOT A FAILURE. Two structural skips are ordinary states — no board-app
//     base URL, and no configured mail template — and in both the seat's link is still minted and
//     returned. Rendering them as errors would send an officer chasing a bug instead of handing over
//     the link, so {@link deliveryLine} says which state it is and what to do.
//   * ENGAGEMENT IS MEASUREMENT, NOT SHAMING (plan §1.7). An open receipt renders as a count and a
//     date, and a seat with none renders as "no opens recorded" — never as a judgement about the
//     director, and never with a comparison against the seats that did.
//
// The MIME allow-list is the backend's (PDF + JPEG/PNG/HEIC/TIFF, deliberately no SVG — a script
// container, and a packet is read by people who did not choose to run one). It is duplicated here only
// to spare a volunteer an upload that the signed-URL issue would refuse anyway; the backend's list is
// authoritative and its refusal is still surfaced verbatim.

import { extractList } from '@givmo/api-client/list';
import { displayInstant, humanize } from '../../lib/display';
export { displayDate, displayInstant } from '../../lib/display';
import { isGivmoError } from '@givmo/api-client';
import type {
  BoardEngagementReceiptOut,
  OrgBoardPacketAnnotationOut,
  BoardPacketDistributionOut,
  BoardPacketDocumentOut,
  BoardPacketItemOut,
  OrgBoardPacketOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

// ── Tolerant list reads ──────────────────────────────────────────────────────────────────────────

/** A meeting's packet VERSIONS, newest first (the list route does not hydrate contents). */
export function extractPackets(body: unknown): OrgBoardPacketOut[] {
  return [...extractList<OrgBoardPacketOut>(body, 'data')].sort(
    (left, right) => right.version - left.version,
  );
}

/** Attachments uploaded against a MEETING — they outlive any one packet version. */
export function extractDocuments(body: unknown): BoardPacketDocumentOut[] {
  return extractList<BoardPacketDocumentOut>(body, 'data');
}

/** Who was sent one packet version. */
export function extractDistributions(body: unknown): BoardPacketDistributionOut[] {
  return extractList<BoardPacketDistributionOut>(body, 'data');
}

/**
 * A packet comment row as the annotation helpers read it: the two fields they select on. Generic, so
 * the director door's rows (its own wire, until SEC2-WEB) pass through them unchanged.
 */
type PacketAnnotationRow = Pick<OrgBoardPacketAnnotationOut, 'visibility' | 'board_member_id'>;

/** Board-visible comments on a packet version. */
export function extractAnnotations<T extends PacketAnnotationRow = OrgBoardPacketAnnotationOut>(
  body: unknown,
): T[] {
  return extractList<T>(body, 'data');
}

/** Open receipts for the chair's engagement view. */
export function extractEngagement(body: unknown): BoardEngagementReceiptOut[] {
  return extractList<BoardEngagementReceiptOut>(body, 'data');
}

// ── Versions ─────────────────────────────────────────────────────────────────────────────────────

/** The one draft version, if a meeting has one. At most one exists — a partial unique index fences it. */
export function draftVersion(packets: readonly OrgBoardPacketOut[]): OrgBoardPacketOut | null {
  return packets.find((packet) => packet.status === 'draft') ?? null;
}

/** The published versions, newest first. Each is frozen; the newest is what the board holds. */
export function publishedVersions(packets: readonly OrgBoardPacketOut[]): OrgBoardPacketOut[] {
  return packets.filter((packet) => packet.status === 'published');
}

/** Whether this version can still be assembled. Only a draft can. */
export function isDraft(packet: OrgBoardPacketOut | null | undefined): boolean {
  return packet?.status === 'draft';
}

/** Human label for a version's state. */
export function packetStatusLabel(status: string | null | undefined): string {
  switch ((status ?? '').toLowerCase()) {
    case 'draft':
      return 'Being assembled';
    case 'published':
      return 'Issued';
    default:
      return humanize(status);
  }
}

/** Badge tone for a version's state. PURELY VISUAL — the words come from {@link packetStatusLabel}. */
export function packetStatusTone(status: string | null | undefined): BadgeTone {
  return (status ?? '').toLowerCase() === 'published' ? 'success' : 'info';
}

// ── Entries ──────────────────────────────────────────────────────────────────────────────────────

/**
 * A version's entries in the order the officer set.
 *
 * Sorted by `position` rather than trusted as delivered, because the reorder route renumbers inside one
 * transaction against a DEFERRED constraint and a read racing that commit could otherwise render a
 * half-applied swap.
 */
export function sortPacketItems(items: readonly BoardPacketItemOut[]): BoardPacketItemOut[] {
  return [...items].sort((left, right) => left.position - right.position);
}

/** Human label for what an entry IS. */
export function itemKindLabel(kind: string | null | undefined): string {
  switch ((kind ?? '').toLowerCase()) {
    case 'agenda_item':
      return 'Agenda item';
    case 'board_record':
      return 'From the minute book';
    case 'document':
      return 'Attachment';
    case 'note':
      return 'Note';
    default:
      return humanize(kind);
  }
}

/**
 * How one entry reads in a list, given the meeting's agenda and attachments.
 *
 * Falls back to the entry's own title, then to the kind — never to an empty row. An entry whose target
 * is not in the lists handed in is rendered by its kind rather than dropped: the reader is looking at a
 * packet the backend already decided they may see, and hiding a row here would make the on-screen
 * contents disagree with what was issued.
 */
export function itemTitle(
  item: BoardPacketItemOut,
  lookups: {
    agendaTitles?: ReadonlyMap<string, string>;
    documentNames?: ReadonlyMap<string, string>;
    recordTitles?: ReadonlyMap<string, string>;
  } = {},
): string {
  const explicit = (item.title ?? '').trim();
  if (explicit.length > 0) return explicit;
  if (item.item_kind === 'agenda_item' && item.agenda_item_id) {
    return lookups.agendaTitles?.get(item.agenda_item_id) ?? 'Agenda item';
  }
  if (item.item_kind === 'document' && item.document_id) {
    return lookups.documentNames?.get(item.document_id) ?? 'Attachment';
  }
  if (item.item_kind === 'board_record' && item.record_id) {
    return lookups.recordTitles?.get(item.record_id) ?? 'A record from the minute book';
  }
  return itemKindLabel(item.item_kind);
}

// ── Attachments ──────────────────────────────────────────────────────────────────────────────────

/**
 * The content types the backend's signed-URL issue accepts, as `<input accept>` values.
 *
 * The signed PUT URL is BOUND to the type it was issued for, so a mismatch fails at storage rather
 * than politely — which is why the picker constrains the choice up front.
 */
export const ATTACHMENT_CONTENT_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/tiff',
];

/** The `accept` attribute for the attachment picker. */
export const ATTACHMENT_ACCEPT = ATTACHMENT_CONTENT_TYPES.join(',');

/** Whether this file's reported type is one the backend will issue a URL for. */
export function isAllowedAttachment(contentType: string | null | undefined): boolean {
  return ATTACHMENT_CONTENT_TYPES.includes((contentType ?? '').toLowerCase());
}

/** Whether this attachment's bytes actually landed. A `pending_upload` row is a started, unfinished one. */
export function isUploaded(attachment: BoardPacketDocumentOut): boolean {
  return attachment.status === 'uploaded';
}

/** A file size a person can read. Returns null when the backend recorded none. */
export function displaySize(bytes: number | null | undefined): string | null {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} bytes`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${Math.round(kilobytes)} KB`;
  return `${(kilobytes / 1024).toFixed(1)} MB`;
}

/**
 * Whether a refusal is "this environment has no document storage configured".
 *
 * Keyed on the HTTP status, deliberately: the backend's 503 handler returns a constant, non-oracle
 * envelope with no error code to match on. It is a real state in every environment without
 * `BOARD_DOCUMENTS_BUCKET_NAME` set, and the honest answer is to say so rather than to offer a retry
 * that cannot succeed.
 */
export function isStorageUnavailable(caught: unknown): boolean {
  return isGivmoError(caught) && caught.status === 503;
}

// ── Distribution ─────────────────────────────────────────────────────────────────────────────────

/** What actually happened for one seat, in words that never read as a failure when none occurred. */
export interface DeliveryLine {
  /** The short state, for a badge. */
  label: string;
  tone: BadgeTone;
  /** The sentence under it, saying what to do about it. Null when nothing needs saying. */
  detail: string | null;
}

/**
 * How one seat's row reads.
 *
 * `email_sent: false` is NOT an error and must never be shown as one. For a member who hasn't joined,
 * both structural skips leave their invitation ready on the roster, so the secretary's own path —
 * hand the director the invitation — is unaffected; a member who has joined gets a notice rather than
 * an invitation (CBG-SEC2, contract §12.4), so there is nothing to hand over. The copy says that,
 * because an officer who reads a red row as a bug chases a phantom while a director waits.
 *
 * The row's `skip_reason` is NOT shown (CBG-FIX14). It is the backend's diagnostic and names a
 * deployment setting ("no board app base url configured"), which means nothing to an officer
 * and is not theirs to change. The sentence says only that no email went, whatever the reason
 * (copy round 2, D-CBG-18: "this environment" is a word for engineers).
 */
export function deliveryLine(
  distribution: BoardPacketDistributionOut,
  { joined = false }: { joined?: boolean } = {},
): DeliveryLine {
  if (distribution.email_sent === true) {
    return { label: 'Emailed', tone: 'success', detail: null };
  }
  return {
    label: 'No email',
    tone: 'neutral',
    detail: joined
      ? 'No email went out.'
      : 'No email went out. Their invitation is ready to hand over.',
  };
}

/** How many times this seat has been sent this version, where the count is worth showing. */
export function distributionCountLine(distribution: BoardPacketDistributionOut): string | null {
  const count = distribution.distribution_count ?? 0;
  if (count <= 1) return null;
  return `Sent ${count} times`;
}

// ── Engagement receipts ──────────────────────────────────────────────────────────────────────────

/**
 * One seat's opens of this packet version, as a neutral sentence.
 *
 * Plan §1.7: engagement visibility is "measurement, not shaming". So this reports a count and a date
 * and stops — no ranking against other seats, no "still hasn't opened it", and no inference about
 * whether the director is prepared. A seat with no receipt gets a plain absence, which is the only
 * true statement available: an open is recorded when the packet page is read, and a director who read
 * their papers on paper leaves none.
 */
export function openReceiptLine(receipt: BoardEngagementReceiptOut | null | undefined): string {
  if (receipt === null || receipt === undefined) return 'No opens recorded';
  const count = receipt.open_count ?? 0;
  if (count <= 0) return 'No opens recorded';
  const when = displayInstant(receipt.last_opened_at ?? receipt.first_opened_at);
  const opens = count === 1 ? 'Opened once' : `Opened ${count} times`;
  return when === '—' ? opens : `${opens} · last ${when}`;
}

/** Receipts by seat, so a roster row can find its own without a scan per render. */
export function receiptsBySeat(
  receipts: readonly BoardEngagementReceiptOut[],
): Map<string, BoardEngagementReceiptOut> {
  return new Map(receipts.map((receipt) => [receipt.board_member_id, receipt]));
}

// ── Annotations ──────────────────────────────────────────────────────────────────────────────────

/**
 * A director's OWN private notes, out of the mixed list the backend returns.
 *
 * The director read carries this seat's private notes plus every board-visible comment, and the two
 * must never be rendered in one list: a note a director wrote for themselves appearing under a heading
 * that says the board can see it is the failure that makes the whole feature worthless. Both halves are
 * selected POSITIVELY (`=== 'private'` / `=== 'board'`) rather than by negation, for the record core's
 * reason — a negative test admits any value a later build adds, in the permissive direction.
 */
export function privateNotes<T extends PacketAnnotationRow>(
  annotations: readonly T[],
  mySeatId: string | null,
): T[] {
  if (mySeatId === null) return [];
  return annotations.filter(
    (annotation) => annotation.visibility === 'private' && annotation.board_member_id === mySeatId,
  );
}

/** The board-visible comments — everything every director on this board can read. */
export function sharedComments<T extends PacketAnnotationRow>(annotations: readonly T[]): T[] {
  return annotations.filter((annotation) => annotation.visibility === 'board');
}

/**
 * How a comment's authorship reads, keeping the officer's on-behalf entry distinguishable.
 *
 * `forTheReader` is the director reading their OWN seat's comment, which the line beside reads as
 * "You": then it is "on your behalf", never "their" (CBG-UX4).
 */
export function recordedByLabel(
  recordedBy: string | null | undefined,
  { forTheReader = false }: { forTheReader?: boolean } = {},
): string | null {
  if ((recordedBy ?? '').toLowerCase() !== 'organization_admin') return null;
  return forTheReader
    ? 'Entered by an officer on your behalf'
    : 'Entered by an officer on their behalf';
}
